import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Col, Row, Skeleton } from 'antd'
import { Formik } from 'formik'
import * as Yup from 'yup'

import { Attachment } from 'core/domain/expenses/attachment'

import CheckboxGroup from 'common/components/dataEntry/formik/FormikCheckboxGroupField'
import Modal from 'common/components/feedback/Modal'
import NoData from 'common/components/feedback/NoData'
import Button from 'common/components/general/Button'

import GalleryItem from './GalleryItem'

import './SelectFromLibrary.less'

const formValidator = (translate: any) =>
  Yup.object({
    galleryItems: Yup.array()
      .min(1, translate('translation:general.required'))
      .typeError(translate('translation:general.required'))
      .required(translate('translation:general.required')),
  })

interface SelectFromLibraryProps {
  visible: boolean
  attachments: Attachment[]
  loadingAttachments: boolean
  deleteAttachment: (id: string) => void
  onCancel: () => void
  setSelectedAttachments: (attachments: Attachment[]) => void
}

const SelectFromLibraryModal: React.FC<SelectFromLibraryProps> = ({
  visible,
  attachments,
  onCancel,
  loadingAttachments,
  deleteAttachment,
  setSelectedAttachments,
}) => {
  const { t: translate } = useTranslation(['ee-dashboard', 'translation'])
  const [isFormValid, setIsFormValid] = useState<boolean>(false)
  const [attachmentToPreview, setAttachmentToPreview] = useState<Attachment | null>(null)

  let submitFormHandler: () => Promise<any> = () => new Promise(() => {})

  interface SelectFromGalleryValues {
    galleryItems: string[]
  }

  const initialValues: SelectFromGalleryValues = {
    galleryItems: [],
  }

  const onSubmit = async (values: SelectFromGalleryValues) => {
    const selectedAttachments = attachments.filter((attachment) =>
      values.galleryItems.some((galleryItem) => galleryItem === attachment.attachmentId),
    )

    setSelectedAttachments(selectedAttachments)
  }

  const modalFooterActions = [
    <Button key="back" onClick={onCancel}>
      {translate('translation:general.cancel')}
    </Button>,
    <Button key="submit" type="primary" disabled={!isFormValid} onClick={() => submitFormHandler()}>
      {translate('translation:general.add')}
    </Button>,
  ]

  return (
    <Modal
      title={translate('get-reimburse.request.select-from-existing-title')}
      visible={visible}
      destroyOnClose
      closable
      onCancel={onCancel}
      footer={modalFooterActions}
    >
      <Skeleton loading={loadingAttachments}>
        <Formik
          enableReinitialize={true}
          validationSchema={formValidator(translate)}
          initialValues={initialValues}
          onSubmit={onSubmit}
          validate={(values) => {
            setIsFormValid(formValidator(translate).isValidSync(values))
          }}
        >
          {(formik) => {
            const { handleSubmit, submitForm } = formik
            submitFormHandler = submitForm
            return (
              <form onSubmit={handleSubmit} autoComplete="off">
                <Row>
                  <Col>
                    <div className="gallery_wrapper">
                      <CheckboxGroup name="galleryItems">
                        {attachments.map((attachment) => (
                          <GalleryItem
                            attachment={attachment}
                            setAttachmentToPreview={setAttachmentToPreview}
                            deleteAttachment={deleteAttachment}
                            translate={translate}
                          />
                        ))}
                      </CheckboxGroup>
                    </div>
                  </Col>
                </Row>
                {attachmentToPreview && (
                  <Modal
                    title={attachmentToPreview.title}
                    visible={attachmentToPreview !== null}
                    footer={null}
                    onCancel={() => setAttachmentToPreview(null)}
                  >
                    <img src={attachmentToPreview.fileNameUrl} className="fit_modal" alt="" />
                  </Modal>
                )}
                <NoData length={attachments.length} />
              </form>
            )
          }}
        </Formik>
      </Skeleton>
    </Modal>
  )
}

export default SelectFromLibraryModal
